/**
 * ApiKeyTable - renders the list of bsk_live_* API keys with row actions.
 *
 * Columns: Name, Prefix, Permissions summary, AI agent badge, Created,
 * Last used, Status (Active / Revoked / Expired), Revoke action.
 *
 * The parent owns the data + revoke mutation; this component is purely
 * presentational so it can be tested in isolation.
 */
import { useState } from "react";

import { Badge, Button } from "@/components/ui";
import type {
  ApiKeyPermissionLevel,
  ApiKeyResponse,
} from "@/types/api";

export type ApiKeyStatus = "active" | "revoked" | "expired";

function deriveStatus(key: ApiKeyResponse): ApiKeyStatus {
  if (key.revoked_at) return "revoked";
  if (key.expires_at && new Date(key.expires_at).getTime() < Date.now()) {
    return "expired";
  }
  return "active";
}

function summarizePermissions(
  permissions: Partial<Record<string, ApiKeyPermissionLevel>>,
): string {
  const parts: string[] = [];
  for (const [resource, level] of Object.entries(permissions)) {
    if (level && level !== "none") {
      parts.push(`${resource}:${level}`);
    }
  }
  return parts.length === 0 ? "No access" : parts.join(", ");
}

function formatDate(iso: string | null): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString();
}

export interface ApiKeyTableProps {
  keys: ApiKeyResponse[];
  onRevoke: (keyId: string) => void;
  isRevoking?: boolean;
}

export function ApiKeyTable({
  keys,
  onRevoke,
  isRevoking = false,
}: ApiKeyTableProps): JSX.Element {
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);

  if (keys.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-8 text-center">
        <i
          className="fa-duotone fa-thin fa-key text-3xl text-neutral-400"
          aria-hidden="true"
        />
        <p className="mt-3 text-sm font-medium text-neutral-900">
          No API keys yet
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          Create one to grant programmatic access to the public API.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
        <table className="min-w-full divide-y divide-neutral-200">
          <thead className="bg-neutral-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Name
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Prefix
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Permissions
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Created
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Last used
              </th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Status
              </th>
              <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {keys.map((key) => {
              const status = deriveStatus(key);
              return (
                <tr key={key.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 text-sm text-neutral-900">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{key.name}</span>
                      {key.is_ai_agent && (
                        <Badge variant="info" className="shrink-0">
                          AI agent
                        </Badge>
                      )}
                    </div>
                    {key.description && (
                      <p className="mt-0.5 text-xs text-neutral-500">
                        {key.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-700">
                    {key.key_prefix}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-700">
                    {summarizePermissions(key.permissions)}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500">
                    {formatDate(key.created_at)}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500">
                    {formatDate(key.last_used_at)}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {status === "active" && (
                      <Badge variant="success">Active</Badge>
                    )}
                    {status === "revoked" && (
                      <Badge variant="error">Revoked</Badge>
                    )}
                    {status === "expired" && (
                      <Badge variant="warning">Expired</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {status === "active" && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPendingRevokeId(key.id)}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pendingRevokeId && (
        <RevokeConfirmDialog
          isOpen
          isLoading={isRevoking}
          onCancel={() => setPendingRevokeId(null)}
          onConfirm={() => {
            const id = pendingRevokeId;
            setPendingRevokeId(null);
            onRevoke(id);
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Inline confirm dialog. Co-located to keep the table self-contained.
// ---------------------------------------------------------------------------

interface RevokeConfirmDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function RevokeConfirmDialog({
  isOpen,
  isLoading,
  onCancel,
  onConfirm,
}: RevokeConfirmDialogProps): JSX.Element | null {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onCancel}
      aria-hidden="true"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-lg border border-neutral-200 bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-neutral-900">
          Revoke this API key?
        </h3>
        <p className="mt-2 text-sm text-neutral-600">
          Revocation is immediate. Any in-flight request using this key will
          fail on its next attempt. This action cannot be undone.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            className="bg-red-600 hover:bg-red-700 active:bg-red-800"
            onClick={onConfirm}
            isLoading={isLoading}
          >
            Revoke
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ApiKeyTable;
